import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Bell, UserPlus, CheckCheck } from 'lucide-react';

const POLL_MS = 60000;

const timeAgo = (iso) => {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff/notifications`);
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch (e) {
      console.debug('NotificationBell: failed to load notifications', e);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    try {
      await axios.post(`${API}/staff/notifications/read-all`);
      load();
    } catch (e) {
      console.debug('NotificationBell: failed to mark all read', e);
    }
  };

  const markOne = async (id) => {
    try {
      await axios.post(`${API}/staff/notifications/${id}/read`);
      load();
    } catch (e) {
      console.debug('NotificationBell: failed to mark notification read', e);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="relative text-white hover:bg-white/15"
          data-testid="notification-bell-button"
          aria-label="Notifications"
          title="Notifications"
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-[10px] font-bold text-white flex items-center justify-center"
              data-testid="notification-unread-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <span className="font-semibold text-slate-900">Notifications</span>
          {unread > 0 && (
            <button
              onClick={markAll}
              className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500" data-testid="notification-empty">
              Nothing new right now.
            </p>
          ) : (
            items.map((n) => (
              <Link
                key={n.id}
                to={n.link || '/staff'}
                onClick={() => { setOpen(false); if (!n.is_read) markOne(n.id); }}
                className={`block px-4 py-3 border-b border-slate-50 hover:bg-slate-50 ${n.is_read ? '' : 'bg-indigo-50/60'}`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex gap-2">
                  <UserPlus className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">{n.title}</div>
                    <div className="text-xs text-slate-600 break-words">{n.message}</div>
                    <div className="text-[11px] text-slate-400 mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
